import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Divider,
  Chip
} from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CloudIcon from '@mui/icons-material/Cloud';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ApiIcon from '@mui/icons-material/Api';
import DescriptionIcon from '@mui/icons-material/Description';

function AboutUs() {
  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#f8f9fa',
      py: 6 
    }}>
      <Container maxWidth="lg">
        {/* Header with Animation */}
        <Box sx={{ 
          mb: 6, 
          textAlign: 'center',
          animation: 'fadeIn 1s ease-in',
          '@keyframes fadeIn': {
            from: { opacity: 0, transform: 'translateY(-20px)' },
            to: { opacity: 1, transform: 'translateY(0)' }
          }
        }}>
          <Typography 
            variant="h2" 
            gutterBottom 
            sx={{ 
              fontWeight: 'bold', 
              color: '#2c3e50',
              mb: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2
            }}
          >
            <span>The New Way to</span>
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                minWidth: '200px',
                textAlign: 'center',
                position: 'relative',
                height: '1.2em'
              }}
            >
              <Box
                component="span"
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  color: '#1976d2',
                  animation: 'wordFade1 9s infinite',
                  '@keyframes wordFade1': {
                    '0%, 30%': { opacity: 1 },
                    '33%, 100%': { opacity: 0 }
                  }
                }}
              >
                Advance
              </Box>
              <Box
                component="span"
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  color: '#7b1fa2',
                  animation: 'wordFade2 9s infinite',
                  '@keyframes wordFade2': {
                    '0%, 30%': { opacity: 0 },
                    '33%, 63%': { opacity: 1 },
                    '66%, 100%': { opacity: 0 }
                  }
                }}
              >
                Progress
              </Box>
              <Box
                component="span"
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  color: '#388e3c',
                  animation: 'wordFade3 9s infinite',
                  '@keyframes wordFade3': {
                    '0%, 63%': { opacity: 0 },
                    '66%, 96%': { opacity: 1 },
                    '100%': { opacity: 0 }
                  }
                }}
              >
                Success
              </Box>
            </Box>
          </Typography>
        </Box>

        {/* Origin Story */}
        <Paper 
          elevation={8} 
          sx={{ 
            p: 5, 
            mb: 4, 
            bgcolor: '#fff',
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '5px',
              background: 'linear-gradient(90deg, #42a5f5 0%, #1976d2 100%)'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <RocketLaunchIcon sx={{ fontSize: 40, color: '#1976d2', mr: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
              Our Journey
            </Typography>
          </Box>
          
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: '#555' }}>
            MiM is an End to End platform which was developed by Cornelius in early 2016 to support the connectivity of the beverage dispensing equipment.
          </Typography>
          
          <Box sx={{ 
            bgcolor: '#e3f2fd', 
            p: 3, 
            borderRadius: 2, 
            border: '2px solid #90caf9',
            my: 3 
          }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold', mb: 2 }}>
              The Challenges We Solved:
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ width: 8, height: 8, bgcolor: '#1976d2', borderRadius: '50%', mr: 2 }} />
                  <Typography variant="body1">Get the marketing data</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ width: 8, height: 8, bgcolor: '#42a5f5', borderRadius: '50%', mr: 2 }} />
                  <Typography variant="body1">Get the diagnostic data</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ width: 8, height: 8, bgcolor: '#1976d2', borderRadius: '50%', mr: 2 }} />
                  <Typography variant="body1">OTA firmware update</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ width: 8, height: 8, bgcolor: '#42a5f5', borderRadius: '50%', mr: 2 }} />
                  <Typography variant="body1">Publish media to device</Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: '#555' }}>
            Marmon looked at multiple platforms to solve these challenges and while there were many good platforms, none of them allowed our business the flexibility needed or the end to end solution that was required. The decision was made to create our own solution to service all of our Marmon companies. This allowed Marmon to create a multi-tenant system built from the ground up addressing <strong>scalability, GDPR and security</strong> out of the gate rather than an afterthought.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: '#555' }}>
            Later in 2019, Marmon identified its capability and brought it up to the Marmon level to support other companies inside Marmon to solve their challenges related to IoT. In <strong>January 2019</strong>, MiM moved from Cornelius to Marmon to support entire Marmon businesses.
          </Typography>
        </Paper>

        {/* Business Adoption */}
        <Paper 
          elevation={8} 
          sx={{ 
            p: 5, 
            mb: 4, 
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            borderRadius: 3,
            border: '1px solid #e0e0e0'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <CloudIcon sx={{ fontSize: 40, color: '#1976d2', mr: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
              Growing Together
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ 
                p: 3, 
                bgcolor: '#fff', 
                borderRadius: 2, 
                borderLeft: '4px solid #42a5f5',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: '#555' }}>
                  In <strong>2019</strong>, the Marmon innovation group developed a platform to manage the inventories in a factory. <strong>Ever Stock</strong> was released in 2019, which is an automated inventory management system.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ 
                p: 3, 
                bgcolor: '#fff', 
                borderRadius: 2, 
                borderLeft: '4px solid #66bb6a',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: '#555' }}>
                  At the same time, Marmon water business started looking at the platform to support their water softener businesses. In <strong>January 2020</strong>, <strong>Ecowater</strong> from Marmon water business developed the <strong>Iqua app</strong>, available in the Apple and Google play stores. The Morton connected units have started shipping to Menards and are expected to start showing up in stores during the first or second week of February.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ 
                p: 3, 
                bgcolor: '#fff', 
                borderRadius: 2, 
                borderLeft: '4px solid #42a5f5',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: '#555' }}>
                  <strong>Angelo Po</strong> and <strong>Prince Castle</strong> were the other businesses in the food sector using Marmon to connect their Oven to MiM.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Key Features */}
        <Box sx={{ mb: 6 }}>
          <Typography 
            variant="h3" 
            gutterBottom 
            sx={{ 
              color: '#2c3e50', 
              fontWeight: 'bold', 
              mb: 5, 
              textAlign: 'center'
            }}
          >
            What We Offer
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{ 
                height: '100%', 
                transition: 'all 0.3s ease',
                background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                border: '2px solid #42a5f5',
                '&:hover': { 
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 12px 24px rgba(25, 118, 210, 0.3)',
                  background: 'linear-gradient(135deg, #bbdefb 0%, #90caf9 100%)'
                } 
              }}>
                <CardContent sx={{ p: 4 }}>
                  <SupportAgentIcon sx={{ fontSize: 50, mb: 2, color: '#1976d2' }} />
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#1565c0' }}>
                    Customer Support
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#424242' }}>
                    Each business has their "End consumer support" available, and they are in touch with MiM support team in case of any API or Device connectivity issues.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ 
                height: '100%', 
                transition: 'all 0.3s ease',
                background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
                border: '2px solid #ba68c8',
                '&:hover': { 
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 12px 24px rgba(123, 31, 162, 0.3)',
                  background: 'linear-gradient(135deg, #e1bee7 0%, #ce93d8 100%)'
                } 
              }}>
                <CardContent sx={{ p: 4 }}>
                  <CloudIcon sx={{ fontSize: 50, mb: 2, color: '#7b1fa2' }} />
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#6a1b9a' }}>
                    DevOps
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#424242' }}>
                    You are in safe hands, MiM DevOps team has good experience in IoT platform and services to fulfil your business requirements.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ 
                height: '100%', 
                transition: 'all 0.3s ease',
                background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
                border: '2px solid #66bb6a',
                '&:hover': { 
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 12px 24px rgba(56, 142, 60, 0.3)',
                  background: 'linear-gradient(135deg, #c8e6c9 0%, #a5d6a7 100%)'
                } 
              }}>
                <CardContent sx={{ p: 4 }}>
                  <SecurityIcon sx={{ fontSize: 50, mb: 2, color: '#388e3c' }} />
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                    Security
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#424242' }}>
                    MiM will handle the connected device's security through the TLS certificates. MiM stores your data using industry standard AES-256 encryption algorithm.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ 
                height: '100%', 
                transition: 'all 0.3s ease',
                background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                border: '2px solid #ffb74d',
                '&:hover': { 
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 12px 24px rgba(245, 124, 0, 0.3)',
                  background: 'linear-gradient(135deg, #ffe0b2 0%, #ffcc80 100%)'
                } 
              }}>
                <CardContent sx={{ p: 4 }}>
                  <VerifiedUserIcon sx={{ fontSize: 50, mb: 2, color: '#f57c00' }} />
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#e65100' }}>
                    GDPR Compliant
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#424242' }}>
                    MiM was designed to be GDPR compliant from the beginning. Consumer data & PII are securely stored and encrypted meeting all requirements.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ 
                height: '100%', 
                transition: 'all 0.3s ease',
                background: 'linear-gradient(135deg, #e1f5fe 0%, #b3e5fc 100%)',
                border: '2px solid #4fc3f7',
                '&:hover': { 
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 12px 24px rgba(2, 136, 209, 0.3)',
                  background: 'linear-gradient(135deg, #b3e5fc 0%, #81d4fa 100%)'
                } 
              }}>
                <CardContent sx={{ p: 4 }}>
                  <ApiIcon sx={{ fontSize: 50, mb: 2, color: '#0288d1' }} />
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#01579b' }}>
                    APIs
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#424242' }}>
                    MiM has APIs to get your data back to your application. MiM follows RESTful API Designing guidelines & The best practices.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ 
                height: '100%', 
                transition: 'all 0.3s ease',
                background: 'linear-gradient(135deg, #fce4ec 0%, #f8bbd0 100%)',
                border: '2px solid #f06292',
                '&:hover': { 
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 12px 24px rgba(194, 24, 91, 0.3)',
                  background: 'linear-gradient(135deg, #f8bbd0 0%, #f48fb1 100%)'
                } 
              }}>
                <CardContent sx={{ p: 4 }}>
                  <DescriptionIcon sx={{ fontSize: 50, mb: 2, color: '#c2185b' }} />
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#880e4f' }}>
                    Documents
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#424242' }}>
                    MiM has good and easy to use documentations to support your application requirements. Look at our API & MQTT documentations.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Contact Information */}
        <Paper 
          elevation={3} 
          sx={{ 
            p: 5, 
            bgcolor: '#fff',
            borderRadius: 3
          }}
        >
          <Typography 
            variant="h4" 
            gutterBottom 
            sx={{ 
              color: '#2c3e50', 
              fontWeight: 'bold', 
              mb: 4,
              textAlign: 'center' 
            }}
          >
            Contact Information
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                p: 4, 
                bgcolor: '#f8f9fa', 
                borderRadius: 3,
                border: '2px solid #e3f2fd',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 16px rgba(25, 118, 210, 0.1)',
                  borderColor: '#90caf9'
                }
              }}>
                <Typography variant="h5" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold', mb: 2 }}>
                  🇮🇳 India Office
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: '#555', lineHeight: 1.8 }}>
                  <strong>Address:</strong><br />
                  Marmon Technologies India Pvt Ltd<br />
                  Brigade Senate, 2<br />
                  Hebbal Kempapura<br />
                  Bengaluru, Karnataka 560024
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                p: 4, 
                bgcolor: '#f8f9fa', 
                borderRadius: 3,
                border: '2px solid #f3e5f5',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 16px rgba(123, 31, 162, 0.1)',
                  borderColor: '#ce93d8'
                }
              }}>
                <Typography variant="h5" gutterBottom sx={{ color: '#7b1fa2', fontWeight: 'bold', mb: 2 }}>
                  🇺🇸 US Office
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: '#555', lineHeight: 1.8 }}>
                  <strong>Address:</strong><br />
                  965 W Chicago Ave<br />
                  Chicago, IL 60642
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ color: '#555' }}>
                  <strong>Email:</strong><br />
                  <a 
                    href="mailto:mim-devops@marmon.com" 
                    style={{ 
                      color: '#1976d2', 
                      textDecoration: 'none',
                      fontWeight: 'bold'
                    }}
                  >
                    mim-devops@marmon.com
                  </a>
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
}

export default AboutUs;
